import type { EntityName, SyncColumns } from '@cadencia/contracts';

/**
 * Clases de entidad y su politica de conflicto (docs/SYNC_ENGINE.md §4).
 * NO se usa last-write-wins global: en las entidades append-only destruiria
 * datos registrados y en las derivadas produciria estados inconsistentes.
 */
export type EntityClass = 'append_only' | 'document' | 'plan' | 'derived';

export const ENTITY_CLASS: Record<EntityName, EntityClass> = {
  profiles: 'document',
  user_preferences: 'document',
  gyms: 'document',
  equipment: 'document',
  // Derivada de `equipment`: ante divergencia se recalcula, no se fusiona.
  available_loads: 'derived',

  // Plan estructurado: lo ejecutado manda sobre lo planificado.
  cardio_prescriptions: 'plan',
  training_blocks: 'plan',
  training_weeks: 'plan',
  training_sessions: 'plan',
  session_exercises: 'plan',
  set_prescriptions: 'plan',
  warmup_prescriptions: 'plan',

  // Hechos registrados: nunca se pisan, la clave natural evita duplicados.
  readiness_checkins: 'append_only',
  performed_sets: 'append_only',
  // Una carrera ocurrio o no ocurrio. Pisarla desde otro dispositivo borraria
  // un hecho, igual que con las series realizadas.
  cardio_logs: 'append_only',
  rest_events: 'append_only',
  load_recommendations: 'append_only',

  // Se crea al empezar y se finaliza al cerrar la sesion: gana la version que
  // la cerro, que es la que tiene `updatedAt` mas reciente.
  workout_logs: 'document',

  // Derivado de los hechos: ante divergencia se recalcula desde performed_sets.
  progression_state: 'derived',

  // El registro de decisiones NO es append-only aunque lo parezca: su estado de
  // aprobacion cambia despues de crearse. Gana la version con `updatedAt` mas
  // reciente, que es la que recogio la respuesta del usuario.
  training_decisions: 'document',

  // Nutricion. El objetivo del dia y el menu son plan; lo comido es un hecho.
  nutrition_targets: 'plan',
  meal_plans: 'plan',
  planned_meals: 'plan',
  // Una comida por hueco y dia. «Comere fuera» y el registro posterior son la
  // misma comida en dos momentos, asi que la fila se corrige en vez de
  // duplicarse: gana la version mas reciente (ADR-038).
  meal_logs: 'document',
  // Un peso de una manana concreta se corrige (mal apuntado, bascula movida),
  // asi que no es append-only: gana la version mas reciente de ese dia.
  daily_weights: 'document',
  user_recipe_settings: 'document',

  // Compra y despensa (docs/SYNC_ENGINE.md §4). La despensa es un documento:
  // «tengo 300 g de arroz» es un estado que se corrige, no un hecho que se
  // acumula. La lista y sus lineas son plan estructurado, como el menu del que
  // salen: si dos dispositivos la regeneran, gana una y no se mezclan a medias.
  pantry_items: 'document',
  shopping_lists: 'plan',
  shopping_list_items: 'plan',

  /*
   * Evidencia. Las dos son DOCUMENTO, y por motivos distintos.
   *
   * Una ficha se corrige —se promueve, se supera, se retracta, se le añade una
   * limitacion al leer el texto completo— asi que gana la version mas reciente.
   * `append_only` habria sido tentador y es falso: el estado cambia.
   *
   * Una busqueda parece un hecho inmutable y tampoco lo es: `kept_count` se
   * cierra cuando termina la evaluacion de lo recuperado, que es despues.
   */
  evidence_searches: 'document',
  evidence_sources: 'document',

  // Progreso. Los dos son documento: una medicion de cinta se corrige cuando
  // se apunto mal, y el check-in de una semana se reescribe si se vuelve a
  // ejecutar. Ni uno ni otro acumulan historial dentro de la misma clave.
  body_measurements: 'document',
  weekly_checkins: 'document',
  // Los pasos de un dia se corrigen —se anoto el numero de ayer en hoy—, igual
  // que el peso: gana la version mas reciente de ese dia.
  daily_activity: 'document',

  // IA. La peticion es un hecho con su factura: ocurrio y costo lo que costo.
  ai_requests: 'append_only',
  // El veredicto sobre una propuesta cambia despues de crearse —la Edge
  // Function la inserta sin juzgar y el dominio la acepta o la rechaza—, asi
  // que gana la version mas reciente, igual que en `training_decisions`.
  ai_decisions: 'document',
  // Contabilidad del mes: se recalcula sumando `ai_requests`, que son los
  // hechos. Ante divergencia manda el servidor, que es quien la escribe.
  ai_usage_budget: 'derived',
};

export type ConflictWinner = 'local' | 'remote' | 'recompute';

export interface ConflictDecision {
  winner: ConflictWinner;
  reasonCodes: string[];
}

/**
 * Resolucion determinista para entidades de clase "documento".
 * Orden de desempate: tombstone > updatedAt > rev > originDeviceId.
 * El desempate por dispositivo no es "justo", pero garantiza que los dos
 * dispositivos converjan al mismo resultado sin volver a comunicarse.
 */
export function resolveDocumentConflict(local: SyncColumns, remote: SyncColumns): ConflictDecision {
  if (local.id !== remote.id) {
    throw new Error('Solo se pueden comparar versiones de la misma fila');
  }

  const localDeleted = local.deletedAt !== null;
  const remoteDeleted = remote.deletedAt !== null;
  if (localDeleted !== remoteDeleted) {
    return {
      winner: localDeleted ? 'local' : 'remote',
      reasonCodes: ['tombstone_wins'],
    };
  }

  const localTime = Date.parse(local.updatedAt);
  const remoteTime = Date.parse(remote.updatedAt);
  if (localTime !== remoteTime) {
    return {
      winner: localTime > remoteTime ? 'local' : 'remote',
      reasonCodes: ['newer_updated_at'],
    };
  }

  if (local.rev !== remote.rev) {
    return { winner: local.rev > remote.rev ? 'local' : 'remote', reasonCodes: ['higher_rev'] };
  }

  if (local.originDeviceId !== remote.originDeviceId) {
    return {
      winner: local.originDeviceId < remote.originDeviceId ? 'local' : 'remote',
      reasonCodes: ['device_id_tiebreak'],
    };
  }

  return { winner: 'remote', reasonCodes: ['identical'] };
}

/** Ventana en la que se admite corregir una fila append-only desde su propio dispositivo. */
export const APPEND_ONLY_CORRECTION_WINDOW_MS = 10 * 60 * 1000;

export function canEditAppendOnly(
  row: SyncColumns,
  editingDeviceId: string,
  nowMs: number,
): boolean {
  if (row.originDeviceId !== editingDeviceId) return false;
  return nowMs - Date.parse(row.createdAt) <= APPEND_ONLY_CORRECTION_WINDOW_MS;
}

/** Punto de entrada unico: decide segun la clase de la entidad. */
export function resolveConflict(
  entity: EntityName,
  local: SyncColumns,
  remote: SyncColumns,
): ConflictDecision {
  const entityClass = ENTITY_CLASS[entity];
  switch (entityClass) {
    case 'document':
    case 'plan':
      return resolveDocumentConflict(local, remote);
    case 'append_only':
      // La clave natural impide duplicados; gana la fila creada antes.
      return {
        winner: Date.parse(local.createdAt) <= Date.parse(remote.createdAt) ? 'local' : 'remote',
        reasonCodes: ['append_only_first_wins'],
      };
    case 'derived':
      return { winner: 'recompute', reasonCodes: ['derived_recomputed_from_facts'] };
    default: {
      const exhaustive: never = entityClass;
      throw new Error(`Clase de entidad no soportada: ${String(exhaustive)}`);
    }
  }
}
